import { c as _c } from "react/compiler-runtime";
import { AccommodationAvailableRoomsDocument, type AccommodationAvailableRoomsQuery } from '@data-access/lib/gql/accommodations/documents';
import { useGraphQL } from '@data-access/lib/graphql-query';
import { trackWebsiteMessage } from '@feat/feat-tracking/message';
import type { PaxParams } from '@feat/feat-url-state/pax';
import { keepPreviousData } from '@tanstack/react-query';
import { formatIsoDate } from '@utils/utils-format';
import { useEffect } from 'react';
import { mapAccommodationPaxInput } from '../_lib/map-accommodation-pax-input';
import { type Dates, getMinMaxLengthOfStay, type RulesOfStay } from './use-app-available-dates';
type Props = {
  paxParams: PaxParams;
  id: number;
  dates: Dates;
  rulesOfStay?: RulesOfStay;
  enabled: boolean;
};
export function useAppCheckAvailableRooms(t0) {
  const $ = _c(19);
  const {
    id,
    dates,
    paxParams,
    enabled
  } = t0;
  let t1;
  if ($[0] !== paxParams) {
    t1 = mapAccommodationPaxInput(paxParams);
    $[0] = paxParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const allocation = t1;
  const [from, to] = dates;
  let t2;
  if ($[2] !== from) {
    t2 = formatIsoDate(from);
    $[2] = from;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== to) {
    t3 = formatIsoDate(to);
    $[4] = to;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== allocation || $[7] !== id || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      input: {
        id,
        allocation,
        checkin: t2,
        checkout: t3
      }
    };
    $[6] = allocation;
    $[7] = id;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== enabled) {
    t5 = {
      enabled,
      select,
      placeholderData: keepPreviousData
    };
    $[11] = enabled;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const result = useGraphQL(AccommodationAvailableRoomsDocument, t4, t5);
  let t6;
  let t7;
  if ($[13] !== from || $[14] !== paxParams || $[15] !== result.data || $[16] !== to) {
    t6 = () => {
      if (!result.data) {
        return;
      }
      const message = result.data.rooms.length === 0 ? "noRoomsAvailable" : "success";
      const {
        adults,
        children
      } = paxParams;
      trackWebsiteMessage("accommodation", {
        from,
        to,
        adults,
        children: children.length,
        message
      });
    };
    t7 = [result.data, from, paxParams, to];
    $[13] = from;
    $[14] = paxParams;
    $[15] = result.data;
    $[16] = to;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  return result;
}
export const areDatesWithinRulesOfStay = (dates: Dates, rulesOfStay?: RulesOfStay) => {
  const [from, to] = dates;
  if (!from || !to || !rulesOfStay) return false;
  const {
    isValid
  } = getMinMaxLengthOfStay(dates, rulesOfStay);
  return isValid;
};
const select = (data: AccommodationAvailableRoomsQuery) => data.availableRooms;